<template>
    <div class="page-container">
        <!-- Alert -->
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-3" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <!-- Page header -->
        <CrmPageHeader
            title="Planes"
            subtitle="Suscripciones y servicios disponibles"
            icon="fas fa-layer-group"
        >
            <soft-button variant="outline" color="secondary" size="sm" @click="getPlans" :loading="spinner">
                <i class="fas fa-sync-alt me-1"></i> Actualizar
            </soft-button>
            <router-link :to="{ name: ROUTES.PLANS_CREATE }">
                <soft-button variant="gradient" size="sm">
                    <i class="fas fa-plus me-1"></i> Nuevo Plan
                </soft-button>
            </router-link>
        </CrmPageHeader>

        <!-- Stats Cards -->
        <div class="row g-3 mb-4">
            <div class="col-xl-3 col-sm-6">
                <div class="stat-card d-flex align-items-center gap-3">
                    <div class="stat-card__icon bg-gradient-primary">
                        <i class="fas fa-layer-group text-white"></i>
                    </div>
                    <div>
                        <p class="stat-card__label">Total Planes</p>
                        <p class="stat-card__value">{{ plans.length }}</p>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-sm-6">
                <div class="stat-card d-flex align-items-center gap-3">
                    <div class="stat-card__icon bg-gradient-success">
                        <i class="fas fa-eye text-white"></i>
                    </div>
                    <div>
                        <p class="stat-card__label">Públicos</p>
                        <p class="stat-card__value">{{ publicPlansCount }}</p>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-sm-6">
                <div class="stat-card d-flex align-items-center gap-3">
                    <div class="stat-card__icon bg-gradient-warning">
                        <i class="fas fa-star text-white"></i>
                    </div>
                    <div>
                        <p class="stat-card__label">Destacados</p>
                        <p class="stat-card__value">{{ featuredPlansCount }}</p>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-sm-6">
                <div class="stat-card d-flex align-items-center gap-3">
                    <div class="stat-card__icon bg-gradient-info">
                        <i class="fas fa-users text-white"></i>
                    </div>
                    <div>
                        <p class="stat-card__label">Total Usuarios</p>
                        <p class="stat-card__value">{{ totalUsersCount }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Plans Table -->
        <div class="row">
            <div class="col-12">
                <div class="crm-card">
                    <div class="crm-card__header d-flex align-items-center justify-content-between">
                        <div>
                            <p style="font-size:.82rem;font-weight:600;color:#1E293B;margin:0">Gestión de Planes</p>
                            <p style="font-size:.72rem;color:#94A3B8;margin:0">Planes de suscripción y servicios</p>
                        </div>
                        <span class="badge bg-light" style="font-size:.75rem;padding:6px 12px">
                            {{ plans.length }} planes
                        </span>
                    </div>

                    <div class="crm-card__body">
                        <div class="table-responsive p-0">
                            <soft-spinner v-if="spinner" />
                            <table v-else class="table align-items-center mb-0">
                                <thead>
                                    <tr>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Orden</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Nombre</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Código</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Precio</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Público</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Destacado</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Usuarios</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!plans || plans.length === 0">
                                        <td colspan="8" class="text-center py-5">
                                            <i class="fas fa-inbox text-secondary mb-2 d-block" style="font-size: 2rem; opacity: 0.4;"></i>
                                            <span class="text-secondary text-sm">No se encontraron planes</span>
                                        </td>
                                    </tr>
                                    <tr v-for="item in sortedPlans" :key="item.id">
                                        <!-- Orden -->
                                        <td>
                                            <div class="d-flex px-2 py-1">
                                                <span class="text-xs font-weight-bold text-secondary">{{ item.order || '—' }}</span>
                                            </div>
                                        </td>
                                        <!-- Nombre -->
                                        <td>
                                            <div class="d-flex align-items-center">
                                                <div>
                                                    <p class="text-sm font-weight-bold mb-0 text-dark">{{ item.name }}</p>
                                                    <p v-if="item.badge_text" class="text-xxs text-secondary mb-0">
                                                        <soft-badge color="info" variant="gradient" size="sm">{{ item.badge_text }}</soft-badge>
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <!-- Código -->
                                        <td>
                                            <span class="badge bg-light text-dark border text-xs">{{ item.code }}</span>
                                        </td>
                                        <!-- Precio -->
                                        <td>
                                            <p class="text-sm font-weight-bold mb-0">{{ formatPrice(item.price) }}</p>
                                        </td>
                                        <!-- Público -->
                                        <td class="text-center">
                                            <soft-badge
                                                :color="item.is_public ? 'success' : 'secondary'"
                                                variant="gradient"
                                                size="sm"
                                            >
                                                {{ item.is_public ? 'Sí' : 'No' }}
                                            </soft-badge>
                                        </td>
                                        <!-- Destacado -->
                                        <td class="text-center">
                                            <soft-badge
                                                :color="item.featured ? 'warning' : 'secondary'"
                                                variant="gradient"
                                                size="sm"
                                            >
                                                <i v-if="item.featured" class="fas fa-star me-1"></i>
                                                {{ item.featured ? 'Sí' : 'No' }}
                                            </soft-badge>
                                        </td>
                                        <!-- Usuarios -->
                                        <td class="text-center">
                                            <span class="text-sm font-weight-bold">{{ item.payment_plans_users_count || 0 }}</span>
                                        </td>
                                        <!-- Acciones -->
                                        <td class="text-center">
                                            <div class="d-flex justify-content-center gap-1">
                                                <router-link
                                                    :to="{ name: ROUTES.PLANS_EDIT, params: { id: item.id } }"
                                                    class="table-action-link"
                                                    title="Editar"
                                                >
                                                    <i class="fas fa-edit"></i>
                                                </router-link>
                                                <a href="#"
                                                    class="table-action-link text-danger"
                                                    @click.prevent="confirmDelete(item.id)"
                                                    title="Eliminar"
                                                >
                                                    <i class="fas fa-trash"></i>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <soft-modal
            @close="closeDeleteConfirm"
            :modalActive="showDeleteConfirm"
            v-if="showDeleteConfirm"
            size="sm"
        >
            <div class="text-center p-3">
                <div class="plan-delete-icon mx-auto mb-3">
                    <i class="fas fa-exclamation-triangle text-danger" style="font-size: 1.75rem;"></i>
                </div>
                <h5 class="font-weight-bolder mb-2">Confirmar Eliminación</h5>
                <p class="text-sm text-secondary mb-2">
                    Esta acción es irreversible. El plan será eliminado permanentemente.
                </p>
                <p class="text-xs text-danger mb-4" v-if="selectedPlanUsersCount > 0">
                    <i class="fas fa-info-circle me-1"></i>
                    Este plan tiene <strong>{{ selectedPlanUsersCount }}</strong> usuario(s) asignado(s). No se podrá eliminar.
                </p>
                <div class="d-flex gap-2 justify-content-center">
                    <soft-button variant="outline" color="secondary" size="sm"
                        @click="closeDeleteConfirm">
                        Cancelar
                    </soft-button>
                    <soft-button variant="gradient" color="danger" size="sm"
                        @click="executeDelete" :loading="spinner">
                        <i class="fas fa-trash me-1"></i>
                        Eliminar
                    </soft-button>
                </div>
            </div>
        </soft-modal>
    </div>
</template>

<script>
import { computed } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftBadge from "@/components/SoftBadge.vue";
import SoftModal from "@/components/SoftModal.vue";
import SoftSpinner from "@/components/SoftSpinner.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import ROUTES from "../../constansRoute";
import { usePlans } from "./usePlans.js";

export default {
    name: "PlansManagement",
    components: {
        SoftButton,
        SoftBadge,
        SoftModal,
        SoftSpinner,
        SoftAlert,
        CrmPageHeader,
    },
    setup() {
        const {
            spinner, plans, alertTable,
            showDeleteConfirm, selectedPlanId,
            getPlans, confirmDelete, executeDelete, closeDeleteConfirm,
            formatPrice,
        } = usePlans();

        getPlans();

        const sortedPlans = computed(() => {
            if (!plans.value) return [];
            return [...plans.value].sort((a, b) => (a.order || 999) - (b.order || 999));
        });

        const publicPlansCount = computed(() => {
            if (!plans.value) return 0;
            return plans.value.filter(p => p.is_public).length;
        });

        const featuredPlansCount = computed(() => {
            if (!plans.value) return 0;
            return plans.value.filter(p => p.featured).length;
        });

        const totalUsersCount = computed(() => {
            if (!plans.value) return 0;
            return plans.value.reduce((sum, p) => sum + (p.payment_plans_users_count || 0), 0);
        });

        const selectedPlanUsersCount = computed(() => {
            if (!selectedPlanId.value || !plans.value) return 0;
            const plan = plans.value.find(p => p.id === selectedPlanId.value);
            return plan?.payment_plans_users_count || 0;
        });

        return {
            ROUTES,
            spinner, plans, alertTable,
            showDeleteConfirm, selectedPlanId,
            getPlans, confirmDelete, executeDelete, closeDeleteConfirm,
            formatPrice,
            sortedPlans, publicPlansCount, featuredPlansCount,
            totalUsersCount, selectedPlanUsersCount,
        };
    },
};
</script>

<style lang="scss" scoped>
.plan-stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
}

.plan-delete-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: center;
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.375rem;
    color: #8392AB;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }

    &.text-danger:hover {
        background-color: #fef2f2;
        color: #ea0606;
    }
}
</style>
